import config from "../config";
import { decryptText, encryptText } from "./OstreonEncryption";

const base_url = config.base_url;

/*
  A partner proposing a change to something their customer already has.

  Nothing here writes to the customer's record. A proposal is queued against
  it and waits; the customer's own save is what moves a value into the record,
  and their refusal is what drops it. That is enforced on the server -- the
  endpoint can write to `partner_changes` and nothing else -- so this module
  only has to be honest about what it sends.

  Each value is encrypted under the CUSTOMER's key, exactly as the customer's
  own fields are. The partner holds a wrap of that key (see Utils/partnerKeys),
  which is what lets them write something their customer can read, and the
  server stores ciphertext it cannot open.
*/

/*
  Which field answers to which permission.

  The authoritative copy of this lives on the server, in
  Ostreon-Backend/Utils/partnerEditable.js, and the two must agree. The server
  refuses anything this file gets wrong, so a disagreement costs the partner a
  rejected save rather than the customer an unwanted change -- but keep them in
  step.

  Asset name and type sit under the description permission: the sharing panel
  offers five rows and neither has one of its own, so rather than invent a
  permission the customer was never shown, they go with the field they are
  read beside.
*/
export const ASSET_EDIT_FIELDS = {
  asset_name: "edit_description",
  asset_type: "edit_description",
  description: "edit_description",

  asset_files: "edit_files",

  financial_exposure: "edit_evaluations",
  financial_exposure_custom_label: "edit_evaluations",
  asset_value: "edit_evaluations",
  currency: "edit_evaluations",
  evaluation_date: "edit_evaluations",
  is_liquid: "edit_evaluations",
  has_associated_debt: "edit_evaluations",
  debt_type: "edit_evaluations",
  debt_amount: "edit_evaluations",
  debt_currency: "edit_evaluations",
  debt_annual_interest: "edit_evaluations",
  debt_payoff_date: "edit_evaluations",
  asset_evaluation_records: "edit_evaluations",
  asset_debt_records: "edit_evaluations",

  beneficiaries: "edit_beneficiaries",
  trustees: "edit_trusted_advisor",
};

/*
  Every partner-sharing flag a beneficiary or an advisor can carry.

  THE canonical list. It is here, in a plain module, because three very
  different places need the same answer and had been keeping their own
  copies: the sharing panel's starting shape, the two modals that rebuild a
  person field by field on save, and decryptBeneficiary/decryptTrustee,
  which rebuild one field by field on every page load.

  Each copy was correct when written and then a row was added. The last
  time, the flags saved correctly and were dropped again on the next
  refresh -- the switches came back off and nothing reported a thing.

  A beneficiary reads the picture and advisor rows and ignores the asset
  ones; an advisor does the reverse. Carrying both on both costs a few
  false booleans and means neither can be missed.
*/
export const PERSON_SHARING_FLAGS = [
  // The master, and the name it carries.
  "share_with_partner",
  "edit_with_partner",
  /*
    Contact details are view-only on both kinds, so no edit flag is ever
    granted -- but the field is still listed, because records written
    before that rule may carry one and it has to survive being read rather
    than vanish and come back as a surprise.
  */
  "share_contact_with_partner",
  "edit_contact_with_partner",
  // A message: a beneficiary's personal message and files, an advisor's
  // additional information.
  "share_message_with_partner",
  "edit_message_with_partner",
  // A beneficiary's photo and the advisors attached to them.
  "share_picture_with_partner",
  "edit_picture_with_partner",
  "share_trustees_with_partner",
  "edit_trustees_with_partner",
  // An advisor's assets and beneficiaries.
  "share_assets_with_partner",
  "edit_assets_with_partner",
  "share_beneficiaries_with_partner",
  "edit_beneficiaries_with_partner",
];

/*
  Those flags off a record, and nothing else.

  `=== true` throughout: a record saved before a flag existed has granted
  nothing, and `undefined` is not a permission.
*/
export const personSharingFlags = (record) =>
  Object.fromEntries(
    PERSON_SHARING_FLAGS.map((flag) => [flag, record?.[flag] === true])
  );

/*
  A beneficiary's fields, one group per row of their sharing panel.

  Two asymmetries, both mirrored from the server's copy
  (Ostreon-Backend/Utils/partnerEditable.js), which is the one that enforces
  them:

  - The contact details are VIEW ONLY. There is no edit flag for them on the
    schema and no switch in that column, so they are absent here and a
    partner cannot propose one however the browser is persuaded to ask.
  - The date of birth has no row, no flag and no entry. It is the one thing
    on a beneficiary that is never shared at all.

  `trustees` IS here, unlike on an advisor, because a beneficiary that shares
  its advisors shares all of them -- the row is the disclosure, exactly as on
  an asset -- so the list the partner edits is the whole list and accepting
  it cannot drop anybody.
*/
export const BENEFICIARY_EDIT_FIELDS = {
  name: "edit_with_partner",
  relationship: "edit_with_partner",
  priority: "edit_with_partner",
  is_minor: "edit_with_partner",

  message: "edit_message_with_partner",
  personal_documents: "edit_message_with_partner",

  profile_avatar: "edit_picture_with_partner",

  trustees: "edit_trustees_with_partner",

  /*
    The assets this beneficiary inherits. Proposable, unlike an advisor's
    attachment lists -- see the server's Utils/partnerEditable.js for why
    this one is safe and those are not. The short version: a partner only
    ever edits the assets shared with them, and the customer's modal merges
    their proposal over that subset instead of replacing the list.
  */
  visible_assets: "edit_assets_with_partner",
};

/*
  An advisor, unchanged: who they are, and how to reach them. Their
  additional information has no flag and therefore no entry, and who they are
  attached to is not theirs to re-link -- `assets` and `beneficiaries` are
  not even stored on the advisor, so accepting one would rewrite other
  records.
*/
export const PERSON_EDIT_FIELDS = {
  name: "edit_with_partner",

  // No contact entry: an advisor's details are view-only, as a
  // beneficiary's are, and no edit flag for them is ever granted.
  additional_information: "edit_message_with_partner",

  assets: "edit_assets_with_partner",
  beneficiaries: "edit_beneficiaries_with_partner",
};

/*
  Which half of a person this partner may change, for the UI -- the two rows
  of their sharing panel.
*/
export const editablePersonSections = (person) => ({
  identity: person?.edit_with_partner === true,
  /*
    Never true for either kind any more: contact details are view-only on a
    beneficiary and on an advisor alike, and no such flag is granted. Kept
    so the modals can keep asking the same question of both.
  */
  contact: person?.edit_contact_with_partner === true,
  message: person?.edit_message_with_partner === true,
  // A beneficiary's.
  picture: person?.edit_picture_with_partner === true,
  trustees: person?.edit_trustees_with_partner === true,
  // An advisor's.
  assets: person?.edit_assets_with_partner === true,
  beneficiaries: person?.edit_beneficiaries_with_partner === true,
});

/*
  Which sections of a person this partner may SEE.

  The mirror of editablePersonSections, and needed separately because a row
  that is shared and empty looks exactly like a row that was never shared:
  both arrive as an empty list. The screen has to tell them apart to know
  whether to offer an "add" control at all -- inert without the edit, but
  present.
*/
export const viewablePersonSections = (person) => ({
  contact: person?.share_contact_with_partner === true,
  message: person?.share_message_with_partner === true,
  // A beneficiary's.
  picture: person?.share_picture_with_partner === true,
  trustees: person?.share_trustees_with_partner === true,
  // An advisor's.
  assets: person?.share_assets_with_partner === true,
  beneficiaries: person?.share_beneficiaries_with_partner === true,
});

const FIELDS_BY_KIND = {
  asset: ASSET_EDIT_FIELDS,
  beneficiary: BENEFICIARY_EDIT_FIELDS,
  trustee: PERSON_EDIT_FIELDS,
};

/*
  May this partner change this field of this record?

  Read off the record itself. The flags arrive on the shared payload already
  resolved -- the server only sends `edit_x: true` when the matching view flag
  is on as well -- so this is one lookup rather than a pair.
*/
export const canEditField = (kind, record, field) => {
  const flag = FIELDS_BY_KIND[kind]?.[field];
  return Boolean(flag) && record?.[flag] === true;
};

// Which whole sections of an asset this partner may change, for the UI.
export const editableSections = (asset) => ({
  description: asset?.edit_description === true,
  files: asset?.edit_files === true,
  evaluations: asset?.edit_evaluations === true,
  beneficiaries: asset?.edit_beneficiaries === true,
  trustees: asset?.edit_trusted_advisor === true,
});

export const canEditAnything = (kind, record) =>
  Object.keys(FIELDS_BY_KIND[kind] || {}).some((field) =>
    canEditField(kind, record, field)
  );

/*
  Compared by what they say, not by what type they happen to be.

  A form hands back "665000" where the record holds 665000, and "true" where
  it holds true. Compared strictly, every open-and-close of a modal would
  propose changes the partner never made -- and the customer would be asked to
  approve a value identical to the one they already have.

  Scalars are therefore compared as text. Anything structured -- a list of
  ids, a records blob -- is compared serialised, where key order is stable
  because both sides come from the same form.
*/
const isScalar = (v) => v === null || v === undefined || typeof v !== "object";
const sameValue = (a, b) =>
  isScalar(a) && isScalar(b)
    ? String(a ?? "") === String(b ?? "")
    : JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/*
  What actually changed, of the fields this partner is allowed to change.

  Anything they are not allowed to change is dropped here rather than sent and
  refused: a disabled field cannot have been edited on purpose, and a stale
  permission in the browser should cost nobody their other work. The server
  refuses the whole proposal if one field is not theirs, so this is also what
  keeps an honest client from tripping over that.
*/
export const changedFields = (kind, original, edited) => {
  const fields = FIELDS_BY_KIND[kind] || {};
  return Object.keys(fields).filter(
    (field) =>
      canEditField(kind, original, field) &&
      Object.prototype.hasOwnProperty.call(edited, field) &&
      !sameValue(edited[field], original[field])
  );
};

/*
  A list of ids a partner proposed, put back together on the customer's side.

  The partner was shown a FILTERED list: /customer/shared intersects an
  asset's beneficiaries with the beneficiaries whose own switch is on, so what
  they edited was missing anyone the customer withheld. Taking their list as
  given would drop those people from the asset -- the customer would accept
  "add one heir" and lose three they were never shown.

  So only the half the partner could see is theirs to replace. Ids the
  customer holds that the partner could not see are kept whatever the proposal
  says, and `couldSee` is the customer's own answer to which is which --
  read off their records, which still carry every flag.

  Order follows the proposal, with the withheld ids after it. Nothing here
  depends on that, and it keeps the list the partner sees intact.
*/
export const mergeProposedIds = (current, proposed, couldSee) => {
  const list = (v) => (Array.isArray(v) ? v.filter((id) => id != null) : []);
  const out = list(proposed);
  const seen = new Set(out.map(String));
  for (const id of list(current)) {
    if (couldSee(id)) continue;
    if (seen.has(String(id))) continue;
    seen.add(String(id));
    out.push(id);
  }
  return out;
};

/*
  Send them.

  One request for the whole set: the server takes a proposal whole or refuses
  it whole, so a partner who has changed four things learns in one answer
  whether all four were queued.
*/
export const proposeChanges = async ({
  customer_id,
  kind,
  record_id,
  fields,
  values,
  customer_key,
  /*
    field -> the ids of files this change names, in plaintext.

    Sent beside the encrypted value because the server has to know those
    files are spoken for: it sweeps a customer's folder for anything their
    records do not reference, and a proposed file is referenced only from
    inside the ciphertext. An id is 32 random characters both sides already
    hold, so naming them discloses nothing.
  */
  files = {},
}) => {
  if (!customer_id || !record_id || !fields?.length) {
    return { ok: false, reason: "nothing to propose" };
  }
  if (!customer_key) return { ok: false, reason: "no key" };

  let changes;
  try {
    changes = await Promise.all(
      fields.map(async (field) => ({
        field,
        // Encrypted the way the customer's own fields are, so they can read it.
        value: await encryptText(customer_key, JSON.stringify(values[field])),
        files: Array.isArray(files[field]) ? files[field] : [],
      }))
    );
  } catch (error) {
    console.log(error);
    return { ok: false, reason: "encryption failed" };
  }

  try {
    const res = await fetch(base_url + "user/partner/customer/propose", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
      },
      credentials: "include",
      body: new URLSearchParams({
        customer_id,
        kind,
        record_id,
        changes: JSON.stringify(changes),
      }).toString(),
    });
    if (res.status === 200) return { ok: true, count: changes.length };
    /*
      The server's reason, carried back rather than flattened into "failed".
      A refusal here is nearly always "that field is not yours to change",
      which the partner can act on; a generic error is not.
    */
    const detail = await res.text().catch(() => "");
    return { ok: false, status: res.status, reason: detail || "refused" };
  } catch (error) {
    console.log(error);
    return { ok: false, reason: String(error?.message || error) };
  }
};

/*
  The customer's side: a proposal, opened.

  Values come back encrypted under this customer's own key, so only they (and
  the partner who wrote it) can read one. A value that will not decrypt is
  dropped rather than shown as ciphertext -- it would be unreadable to the
  customer either way, and a field they cannot read is a field they cannot
  judge.
*/
export const readProposals = async (record, private_key) => {
  const list = Array.isArray(record?.partner_changes)
    ? record.partner_changes
    : [];
  if (!list.length || !private_key) return {};
  const entries = await Promise.all(
    list.map(async (change) => {
      try {
        const text = await decryptText(private_key, change.value);
        return [change.field, { value: JSON.parse(text), at: change.proposed_at }];
      } catch (error) {
        console.log(error);
        return null;
      }
    })
  );
  return Object.fromEntries(entries.filter(Boolean));
};

export const hasProposals = (record) =>
  Array.isArray(record?.partner_changes) && record.partner_changes.length > 0;
